import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import React, { useCallback, useMemo } from "react";

import { useContentAreaSize } from "../../size-context";

import ExamplesSelector from "@/components/ExamplesSelector";
import "./styles.css";

const parseSelectedExamples = (examples: string | number | string[] | undefined) => {
  if (typeof examples === "string") {
    return (
      examples
        ?.split(",")
        .filter(id => id !== "")
        .map((id: string) => ({
          id: Number(id),
          value: {},
          text: "Example " + id,
          category: "Category A",
          difficulty: "Easy",
        })) || []
    );
  }

  if (typeof examples === "number") {
    return [
      {
        id: examples,
        value: {},
        text: "Example " + examples,
        category: "Category A",
        difficulty: "Easy",
      },
    ];
  }

  return (
    examples?.map((id: string) => ({
      id: Number(id),
      value: {},
      text: "Example " + id,
      category: "Category A",
      difficulty: "Easy",
    })) ?? []
  );
};

const parseVisibleColumns = (columns: string | string[] | undefined): string[] => {
  if (typeof columns === "string") {
    return columns.split(",");
  }

  return columns ?? [];
};

const getExamplesSelectorSize = (size: string): "small" | "medium" => {
  if (size === "xs" || size === "sm") {
    return "small";
  }

  return "medium";
};

const getSizeClassName = (size: string): string => {
  if (size === "xs") {
    return "text-[10px]";
  }

  if (size === "sm") {
    return "text-xs";
  }

  return "";
};

const DatasetExamplesDisplay = React.memo((props: NodeViewProps) => {
  const { dataset, version, examples, columns, dataType } = props.node.attrs;

  const initialConfig = useMemo(
    () => ({
      selectedDataset: dataset || "",
      selectedVersion: version || "",
      selectedExamples: parseSelectedExamples(examples),
      visibleColumns: parseVisibleColumns(columns),
      dataType: dataType || "json",
    }),
    [dataset, version, examples, columns, dataType],
  );

  const handleChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (attrs: any) => {
      props.updateAttributes({
        dataset: attrs.selectedDataset,
        version: attrs.selectedVersion,
        examples: attrs.selectedExamples?.map(({ id }: { id: number }) => id),
        columns: attrs.visibleColumns,
        dataType: attrs.dataType,
      });
    },
    [props],
  );

  const { isEditable } = props.editor;
  const size = useContentAreaSize();

  const examplesSelectorSize = getExamplesSelectorSize(size);
  const sizeClassName = getSizeClassName(size);

  return (
    <NodeViewWrapper className={`node-dataset-examples inline-flex ${sizeClassName}`}>
      <ExamplesSelector
        readOnly={!isEditable}
        initialConfig={initialConfig}
        showCollapsible
        onChange={handleChange}
        size={examplesSelectorSize}
      />
    </NodeViewWrapper>
  );
});

DatasetExamplesDisplay.displayName = "DatasetExamplesDisplay";

export default DatasetExamplesDisplay;
